import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TrendingDown, TrendingUp, Minus, HelpCircle } from "lucide-react";
import { Card, SectionLabel } from "@/components/ui/Card";
import type { WeightSummary } from "@/types";

const TREND_META: Record<string, { icon: typeof TrendingDown; cls: string; key: string }> = {
  decreasing: { icon: TrendingDown, cls: "text-green-700", key: "weight.trend_decreasing" },
  rapidly_decreasing: { icon: TrendingDown, cls: "text-coral-dark", key: "weight.trend_rapidly_decreasing" },
  stable: { icon: Minus, cls: "text-taupe", key: "weight.trend_stable" },
  increasing: { icon: TrendingUp, cls: "text-amber-dark", key: "weight.trend_increasing" },
  rapidly_increasing: { icon: TrendingUp, cls: "text-coral-dark", key: "weight.trend_rapidly_increasing" },
  no_data: { icon: HelpCircle, cls: "text-taupe", key: "weight.trend_no_data" },
};

export function WeightTracker({ weight }: { weight: WeightSummary | null }) {
  const { t } = useTranslation();
  if (!weight) return null;
  const meta = TREND_META[weight.trend] || TREND_META.no_data;
  const Icon = meta.icon;

  const progressPct = (() => {
    if (!weight.current_kg || !weight.target_kg || !weight.month_ago_kg) return null;
    const totalGap = weight.month_ago_kg - weight.target_kg;
    if (totalGap <= 0) return null;
    const done = weight.month_ago_kg - weight.current_kg;
    return Math.max(0, Math.min(100, Math.round((done / totalGap) * 100)));
  })();

  return (
    <Card>
      <div className="flex items-center justify-between">
        <SectionLabel>{t("weight.title")}</SectionLabel>
        <Link to="/nutrition" className="text-[11px] text-amber-dark hover:underline">
          {t("weight.log_link")}
        </Link>
      </div>

      {weight.current_kg == null ? (
        <div className="mt-2 text-[13px] text-taupe">
          {t("weight.empty")}{" "}
          <Link to="/nutrition" className="text-amber-dark hover:underline">
            {t("weight.record_link")}
          </Link>
        </div>
      ) : (
        <>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="metric-number text-[28px] text-navy-deep">
              {weight.current_kg}
            </span>
            <span className="text-[12px] text-taupe">{t("weight.kg")}</span>
            {weight.target_kg != null ? (
              <span className="ml-auto text-[11px] text-taupe">
                {t("weight.target", { n: weight.target_kg })}
              </span>
            ) : null}
          </div>

          <div className="mt-1 flex items-center gap-1.5 text-[12px]">
            <Icon className={`h-3.5 w-3.5 ${meta.cls}`} strokeWidth={2} />
            <span className={meta.cls}>{t(meta.key)}</span>
            {weight.week_avg_kg != null ? (
              <span className="text-taupe">
                · {t("weight.avg7d")} <span className="metric-number">{weight.week_avg_kg}</span>
              </span>
            ) : null}
          </div>

          {progressPct != null ? (
            <div className="mt-3">
              <div className="mb-1 flex justify-between text-[10px] text-taupe">
                <span>{t("weight.progress")}</span>
                <span className="metric-number">{progressPct}%</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-cream-border">
                <div
                  className="h-full rounded-full bg-amber"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}
